import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { GenericSvg, SVGName } from "../../../../assets/svgs/GenericSVG";
import { CustomButton } from "../../../../core/components/buttons/CustomButton";
import { getUserAnswerToQuestion } from "../../../../core/store/currentFormSlice";
import { useMainStore } from "../../../../core/store/mainStore";
import { SMALL_MOBILE_BREAKPOINT } from "../../../../core/styles/breakpoints";
import { Body } from "../../../../core/styles/stringStyles";
import { useStyles } from "../../../../core/styles/theme";
import { hexWithOpacity } from "../../../../core/utils/helpers";
import { SelectionCanvas, SVGSelectionCanvas } from "./SelectionCanvas";

type Props = {
  answerId: string;
  questionId: string;
};

export const BodyAreaSelection = ({ answerId, questionId }: Props) => {
  const selectedBodyAreasDetails = useMainStore((state) =>
    getUserAnswerToQuestion(state, answerId),
  )?.bodySelectionDetails;
  const isFemale = false;
  const setUserAnswerToQuestion = useMainStore(
    (state) => state.setUserAnswerToQuestion,
  );
  const deleteUserAnswerToQuestion = useMainStore(
    (state) => state.deleteUserAnswerToQuestion,
  );

  const theme = useStyles();
  const { t } = useTranslation();
  const canvasArray: SVGSelectionCanvas[] = [
    {
      id: isFemale ? "humanFemaleFront" : "humanMaleFront",
      svg: isFemale
        ? ("HumanFemaleFront" as SVGName)
        : ("HumanMaleFront" as SVGName),
      name: t("BODY_ORIENTATION_FRONT"),
      showOrientation: true,
      height: 444,
      width: 194,
    },
    {
      id: isFemale ? "humanFemaleBack" : "humanMaleBack",
      svg: isFemale
        ? ("HumanFemaleBack" as SVGName)
        : ("HumanMaleBack" as SVGName),
      name: t("BODY_ORIENTATION_BACK"),
      showOrientation: true,
      height: 444,
      width: 194,
    },
    {
      id: isFemale ? "humanFemaleRight" : "humanMaleRight",
      svg: isFemale
        ? ("HumanFemaleRight" as SVGName)
        : ("HumanMaleRight" as SVGName),
      name: t("BODY_ORIENTATION_RIGHT"),
      height: 444,
      width: 194,
    },

    {
      id: isFemale ? "humanFemaleLeft" : "humanMaleLeft",
      svg: isFemale
        ? ("HumanFemaleLeft" as SVGName)
        : ("HumanMaleLeft" as SVGName),
      name: t("BODY_ORIENTATION_LEFT"),
      height: 444,
      width: 194,
    },
    {
      id: "humanHeadFront",
      svg: "HumanHeadFront" as SVGName,
      name: t("BODY_ORIENTATION_FRONT"),
      showOrientation: true,
      height: 444,
      width: 194,
    },
    {
      id: "humanHeadBack",
      svg: "HumanHeadBack" as SVGName,
      name: t("BODY_ORIENTATION_BACK"),
      showOrientation: true,
      height: 444,
      width: 194,
    },
    {
      id: "humanHeadLeft",
      svg: "HumanHeadLeft" as SVGName,
      name: t("BODY_ORIENTATION_LEFT"),
      height: 444,
      width: 194,
    },
    {
      id: "humanHeadRight",
      svg: "HumanHeadRight" as SVGName,
      name: t("BODY_ORIENTATION_RIGHT"),
      height: 444,
      width: 194,
    },
  ];

  const [selectedSVGIndex, setSelectedSVGIndex] = useState<number>(0);
  const handleSelectedPoints = (
    id: string,
    points: { x: number; y: number }[],
  ) => {
    const newInfo = selectedBodyAreasDetails
      ? { ...selectedBodyAreasDetails }
      : {};
    if (points.length === 0) {
      delete newInfo[id];
      const patientAnswer = {
        id: answerId,
        bodySelectionDetails: newInfo,
      };
      if (Object.keys(newInfo).length === 0) {
        deleteUserAnswerToQuestion(questionId, answerId);
      } else {
        setUserAnswerToQuestion(questionId, patientAnswer, answerId);
      }
      return;
    }
    newInfo[id] = {
      canvasId: selectedCanvas.id,
      selectedPoints: points,
    };

    const patientAnswer = {
      id: answerId,
      bodySelectionDetails: newInfo,
    };

    setUserAnswerToQuestion(questionId, patientAnswer, answerId);
  };
  const selectedCanvas = canvasArray[selectedSVGIndex];
  const selectedPoints =
    selectedBodyAreasDetails?.[selectedCanvas.id]?.selectedPoints ?? [];

  return (
    <Container>
      {selectedCanvas && (
        <SelectionCanvas
          svgInfo={{ ...selectedCanvas, selectedPoints }}
          setSelectedPoints={handleSelectedPoints}
        />
      )}
      <CanvasSwitcher>
        {canvasArray.map((canvas, index) => (
          <CanvasButton
            key={canvas.id}
            onClick={() => setSelectedSVGIndex(index)}
            $isSelected={selectedSVGIndex === index}
          >
            <GenericSvg
              name={canvas.svg}
              primaryColor={hexWithOpacity(theme.colors.light, 0.8)}
            />

            <StyledBody>{canvas.name}</StyledBody>
          </CanvasButton>
        ))}
      </CanvasSwitcher>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 20px;
`;

const CanvasSwitcher = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 5px;
  height: 100%;
  @media (max-width: ${SMALL_MOBILE_BREAKPOINT}) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const CanvasButton = styled(CustomButton)<{ $isSelected: boolean }>`
  display: flex;
  height: 70px;
  width: 70px;
  justify-content: center;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 8px;
  border-radius: 10px;
  border-color: ${({ theme, $isSelected }) =>
    $isSelected
      ? theme.colors.primary
      : hexWithOpacity(theme.colors.medium, 0.1)};
  border-width: 2px;
  border-style: solid;
`;

const StyledBody = styled(Body)`
  color: ${({ theme }) => theme.colors.medium};
  height: 20px;
`;
